import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../lib/useApi';
import { BarChart, Donut } from '../lib/charts';
import { Card, CardHead, Empty, ErrorState, Kpi, Loading, Section } from '../components/ui';
import { fmtDate, fmtMonth, n0, pct, plural, rub } from '../lib/format';
import { planColor } from '../lib/plans';
import WorkspaceDrawer from './WorkspaceDrawer';

export default function Money({ days, nonce }) {
  const [openId, setOpenId] = useState(null);
  const plans = useApi(() => api.plans(), []);
  const rev = useApi(() => api.revenue({ days }), [days, nonce]);
  const cohorts = useApi(() => api.trialCohorts(), [nonce]);
  const pay = useApi(() => api.payments({ days }), [days, nonce]);
  const renew = useApi(() => api.renewals({ days: 14 }), [nonce]);

  if (rev.loading || plans.loading) return <Loading what="деньги" />;
  if (rev.error) return <ErrorState error={rev.error} onRetry={rev.reload} />;

  const r = rev.data;
  const planList = plans.data || [];
  const color = (name) => planColor(planList, name);

  return (
    <>
      <div className="grid cards-4">
        <Kpi label="выручка в месяц (MRR)" value={n0(r.mrr)} unit="₽" note={`${n0(r.payingCount)} платящих РП`} />
        <Kpi label="средний чек" value={n0(r.avgCheck)} unit="₽" note="по платящим РП" />
        <Kpi label="доплаты за места и гигабайты" value={n0(r.addonsMrr)} unit="₽/мес"
          note={`${n0(r.addonsCount)} РП докупают`} />
        <Kpi label="ушли (истекли или на удалении)" value={n0(r.churned)} unit="РП"
          note={`${pct(r.churned / r.totalWorkspaces)} от всех`} />
      </div>

      <div className="grid cards-2" style={{ marginTop: 12 }}>
        <Card className="pad">
          <CardHead title="из чего складывается MRR" caption="только оплаченные подписки, с учётом годовой скидки 15%" />
          <Donut parts={r.byPlan.filter((p) => p.mrr).map((p) => ({ l: p.plan, v: p.mrr, c: color(p.plan) }))} />
          <div className="legend">
            <span>годовых оплат: {n0(r.annual)}</span>
            <span>месячных: {n0(r.monthly)}</span>
            <span>бесплатных подписок: {n0(r.freeActive)}</span>
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="кто на каком тарифе" caption="живые РП: оплаченные и в триале" />
          <Donut parts={r.countsByPlan.filter((p) => p.count).map((p) => ({ l: p.plan, v: p.count, c: color(p.plan) }))} />
          <div className="legend">
            {planList.map((p) => (
              <span key={p.id}>
                <i style={{ background: color(p.name) }} />
                {p.name} — {p.price ? rub(p.price) + '/мес' : 'бесплатно'}{p.published ? '' : ' · не на витрине'}
              </span>
            ))}
          </div>
        </Card>
      </div>

      <Section title="конверсия триалов" hint="по месяцу, в котором триал начался; открытые триалы из знаменателя исключены" />
      <Card className="pad">
        {cohorts.loading ? <Loading what="когорты" />
          : cohorts.error ? <ErrorState error={cohorts.error} onRetry={cohorts.reload} />
          : (
            <>
              <BarChart height={160} color="#9a6aff" fmt={(v) => n0(v) + '%'}
                data={cohorts.data.map((c) => ({
                  label: new Date(c.date).toLocaleDateString('ru-RU', { month: 'short' }),
                  v: Math.round((c.conversion || 0) * 100),
                }))} />
              <div className="tablecard" style={{ marginTop: 14, background: 'none', border: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th>когорта</th><th className="num">начали триал</th><th className="num">оплатили</th>
                      <th className="num">не оплатили</th><th className="num">ещё идёт</th><th className="num">конверсия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...cohorts.data].reverse().map((c) => (
                      <tr key={c.key} style={{ cursor: 'default' }}>
                        <td>{fmtMonth(c.date)}</td>
                        <td className="num">{c.started}</td>
                        <td className="num" style={{ color: 'var(--ok)' }}>{c.paid}</td>
                        <td className="num" style={{ color: 'var(--amber)' }}>{c.lost}</td>
                        <td className="num muted">{c.open}</td>
                        <td className="num">{c.conversion === null ? <span className="muted">—</span> : pct(c.conversion)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </Card>

      <div className="grid cards-2" style={{ marginTop: 12 }}>
        <Card className="pad">
          <CardHead title={`платежи за ${days} ${plural(days, 'день', 'дня', 'дней')}`} caption="эквайринг Т-Банк" />
          {pay.loading ? <Loading what="платежи" />
            : pay.error ? <ErrorState error={pay.error} onRetry={pay.reload} />
            : (
              <div className="stat-lines">
                <div className="sl"><span className="sl-v" style={{ color: 'var(--ok)' }}>{n0(pay.data.ok)}</span>
                  <span className="sl-l">прошли</span></div>
                <div className="sl"><span className="sl-v" style={{ color: 'var(--bad)' }}>{n0(pay.data.failed)}</span>
                  <span className="sl-l">не прошли — {pct(pay.data.failureRate)} попыток<br />
                    <span className="muted">{pay.data.reasons.map((x) => x.reason).join(', ')}</span></span></div>
                <div className="sl"><span className="sl-v">{n0(pay.data.avgAmount)}</span>
                  <span className="sl-l">средний платёж, ₽</span></div>
              </div>
            )}
          <div className="note" style={{ marginTop: 14 }}>
            Чеков по 54-ФЗ пока нет — до подключения кассы здесь только эквайринг.
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="продлится в ближайшие 2 недели"
            caption={renew.data ? `${n0(renew.data.length)} ${plural(renew.data.length, 'подписка', 'подписки', 'подписок')} · ${rub(renew.data.reduce((s, x) => s + x.amount, 0))}` : ''} />
          {renew.loading ? <Loading what="продления" />
            : renew.error ? <ErrorState error={renew.error} onRetry={renew.reload} />
            : renew.data.length ? (
              <div className="list">
                {renew.data.slice(0, 8).map((o) => (
                  <button className="row" key={o.id} onClick={() => setOpenId(o.id)}>
                    <span className="rn">{o.title}<div className="t-sub">{o.plan}{o.annual ? ' · год' : ''}</div></span>
                    <span className="rv">{fmtDate(o.date)} · {n0(o.amount)} ₽</span>
                  </button>
                ))}
              </div>
            ) : <Empty>в этот период продлений нет</Empty>}
        </Card>
      </div>

      <WorkspaceDrawer id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}
